<script setup lang="ts">
import { computed, onMounted, reactive, ref } from "vue";
import {
  adjustInventory,
  getInventory,
  initializeInventory,
  listInventoryLogs,
  type AdjustmentType,
  type InventoryLog,
  type InventoryView as InventoryBalance,
} from "../api/inventory";
import { getProduct, listProducts, type ProductDetail, type ProductSummary } from "../api/products";

type InventorySku = ProductDetail["skus"][number];
type InventoryRowStatus = "AVAILABLE" | "SOLD_OUT" | "UNINITIALIZED";
type InventoryStatusFilter = "ALL" | InventoryRowStatus;

const products = ref<ProductSummary[]>([]);
const product = ref<ProductDetail | null>(null);
const balances = reactive<Record<string, InventoryBalance | null>>({});
const logs = reactive<Record<string, InventoryLog[]>>({});
const forms = reactive<
  Record<string, { initialQty: number; type: AdjustmentType; changeQty: number; reason: string }>
>({});
const busy = ref(false);
const message = ref("");
const searchQuery = ref("");
const statusFilter = ref<InventoryStatusFilter>("ALL");
const expandedSkuId = ref<string | null>(null);
const activeSkus = computed(() => (product.value?.skus ?? []).filter((sku) => sku.status === "ACTIVE"));

function formFor(skuId: string) {
  if (!forms[skuId]) {
    forms[skuId] = { initialQty: 0, type: "ADJUST", changeQty: 0, reason: "人工调整" };
  }
  return forms[skuId];
}

async function run(task: () => Promise<void>): Promise<void> {
  busy.value = true;
  message.value = "";
  try {
    await task();
  } catch (error) {
    message.value = error instanceof Error ? error.message : "库存操作失败";
  } finally {
    busy.value = false;
  }
}

async function loadProduct(productId: string): Promise<void> {
  searchQuery.value = "";
  statusFilter.value = "ALL";
  expandedSkuId.value = null;
  product.value = await getProduct(productId);
  await Promise.all(
    activeSkus.value.map(async (sku) => {
      balances[sku.id] = await getInventory(sku.id);
      formFor(sku.id);
    }),
  );
}

async function initialize(skuId: string): Promise<void> {
  await run(async () => {
    const form = formFor(skuId);
    balances[skuId] = await initializeInventory(skuId, form.initialQty, "后台首次库存初始化");
    await loadLogs(skuId);
    message.value = "库存初始化完成";
  });
}

async function adjust(skuId: string): Promise<void> {
  await run(async () => {
    const form = formFor(skuId);
    balances[skuId] = await adjustInventory(skuId, {
      type: form.type,
      changeQty: form.changeQty,
      reason: form.reason.trim(),
    });
    await loadLogs(skuId);
    message.value = "库存调整完成，流水已记录";
  });
}

async function loadLogs(skuId: string): Promise<void> {
  logs[skuId] = await listInventoryLogs(skuId);
}

async function openLogs(skuId: string): Promise<void> {
  if (logs[skuId] === undefined) await loadLogs(skuId);
}

function toggleOperations(skuId: string): void {
  expandedSkuId.value = expandedSkuId.value === skuId ? null : skuId;
}

function specText(sku: InventorySku): string {
  const variantValues = sku.attributeValues
    .filter((value) => value.isVariantAttribute && value.displayValue.trim())
    .map((value) => value.displayValue.trim());
  if (variantValues.length) return variantValues.join(" / ");

  const summaryValues = Object.entries(sku.specSummary)
    .filter(([key, value]) => {
      const normalizedKey = key.replace(/[\s_-]/g, "").toUpperCase();
      return Boolean(value.trim()) && normalizedKey !== "MATERIAL" && key !== "材质";
    })
    .map(([, value]) => value.trim());
  return summaryValues.join(" / ") || "默认规格";
}

function rowStatus(sku: InventorySku): InventoryRowStatus {
  const balance = balances[sku.id];
  if (balance == null) return "UNINITIALIZED";
  return balance.availability;
}

function statusText(status: InventoryRowStatus): string {
  if (status === "AVAILABLE") return "正常";
  if (status === "SOLD_OUT") return "售罄";
  return "未初始化";
}

function statusClass(status: InventoryRowStatus): string {
  return `status-badge--${status.toLowerCase()}`;
}

function formatQty(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("zh-CN");
}

function formatTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function logTypeText(type: InventoryLog["type"]): string {
  if (type === "INIT") return "初始化";
  if (type === "DAMAGE") return "损坏";
  if (type === "LOSS") return "盘亏";
  if (type === "PROFIT") return "盘盈";
  return "人工调整";
}

const summary = computed(() => {
  const skus = activeSkus.value;
  const initializedBalances = skus
    .map((sku) => balances[sku.id])
    .filter((balance): balance is InventoryBalance => balance != null);

  return {
    skuCount: skus.length,
    initializedCount: initializedBalances.length,
    uninitializedCount: skus.length - initializedBalances.length,
    actualQty: initializedBalances.reduce((total, balance) => total + balance.actualQty, 0),
    availableQty: initializedBalances.reduce((total, balance) => total + balance.availableQty, 0),
  };
});

const filteredSkus = computed(() => {
  const query = searchQuery.value.trim().toLocaleLowerCase();
  const currentProduct = product.value;

  return activeSkus.value.filter((sku) => {
    const searchableText = [currentProduct?.productNo, currentProduct?.name, sku.skuNo, specText(sku)]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase();
    const matchesSearch = !query || searchableText.includes(query);
    const matchesStatus = statusFilter.value === "ALL" || rowStatus(sku) === statusFilter.value;
    return matchesSearch && matchesStatus;
  });
});

onMounted(() =>
  run(async () => {
    products.value = await listProducts();
    if (products.value[0]) await loadProduct(products.value[0].id);
  }),
);
</script>

<template>
  <section class="inventory-page">
    <header class="page-header inventory-header">
      <div>
        <h1>库存管理</h1>
        <p class="muted">
          当前商品的 SKU 库存工作台；库存数量统一使用“个”。
        </p>
      </div>
      <label
        v-if="products.length"
        class="product-selector"
      >
        <span>当前商品</span>
        <select
          :value="product?.id"
          aria-label="选择当前商品"
          @change="loadProduct(($event.target as HTMLSelectElement).value)"
        >
          <option
            v-for="item in products"
            :key="item.id"
            :value="item.id"
          >
            {{ item.productNo }} · {{ item.name }}
          </option>
        </select>
      </label>
    </header>

    <p
      v-if="message"
      class="inventory-notice"
      role="status"
    >
      {{ message }}
    </p>

    <p
      v-if="busy && !product"
      class="inventory-loading"
      role="status"
    >
      正在加载库存数据…
    </p>

    <template v-if="product">
      <section
        aria-label="库存统计"
        class="inventory-stats"
      >
        <article class="metric-card">
          <div class="metric-card__topline">
            <span>当前商品 SKU</span>
            <span class="metric-card__tag">范围</span>
          </div>
          <strong>{{ summary.skuCount }}</strong>
          <small>已初始化 {{ summary.initializedCount }} 个 · 未初始化 {{ summary.uninitializedCount }} 个</small>
        </article>
        <article class="metric-card metric-card--actual">
          <div class="metric-card__topline">
            <span>实际库存合计</span>
            <span class="metric-card__tag">已加载</span>
          </div>
          <strong>{{ formatQty(summary.actualQty) }}</strong>
          <small>来自当前商品已加载 SKU 的实际库存</small>
        </article>
        <article class="metric-card metric-card--available">
          <div class="metric-card__topline">
            <span>可用库存合计</span>
            <span class="metric-card__tag">可售</span>
          </div>
          <strong>{{ formatQty(summary.availableQty) }}</strong>
          <small>实际库存扣除当前锁定库存后的可用数量</small>
        </article>
      </section>

      <section class="inventory-table-card">
        <div class="inventory-table-card__header">
          <div>
            <h2>SKU 库存明细</h2>
            <p class="muted">
              {{ product.productNo }} · {{ product.name }} · 共 {{ activeSkus.length }} 个 SKU
            </p>
          </div>
          <div class="inventory-filters">
            <label class="search-field">
              <span
                aria-hidden="true"
                class="search-field__icon"
              />
              <input
                v-model="searchQuery"
                aria-label="搜索当前商品 SKU"
                placeholder="搜索编码、名称、SKU 或规格…"
                type="search"
              >
            </label>
            <label class="status-filter">
              <span>状态</span>
              <select
                v-model="statusFilter"
                aria-label="按库存状态筛选"
              >
                <option value="ALL">全部</option>
                <option value="AVAILABLE">正常</option>
                <option value="SOLD_OUT">售罄</option>
                <option value="UNINITIALIZED">未初始化</option>
              </select>
            </label>
          </div>
        </div>

        <div class="inventory-table-wrap">
          <table class="inventory-table">
            <colgroup>
              <col class="inventory-table__col--code">
              <col class="inventory-table__col--name">
              <col class="inventory-table__col--spec">
              <col class="inventory-table__col--actual">
              <col class="inventory-table__col--locked">
              <col class="inventory-table__col--available">
              <col class="inventory-table__col--unit">
              <col class="inventory-table__col--status">
              <col class="inventory-table__col--actions">
            </colgroup>
            <thead>
              <tr>
                <th
                  class="inventory-table__align-left"
                  scope="col"
                >
                  商品编码
                </th>
                <th
                  class="inventory-table__align-left"
                  scope="col"
                >
                  商品名称
                </th>
                <th
                  class="inventory-table__align-left"
                  scope="col"
                >
                  规格
                </th>
                <th
                  class="inventory-table__align-center"
                  scope="col"
                >
                  实际库存
                </th>
                <th
                  class="inventory-table__align-center"
                  scope="col"
                >
                  锁定库存
                </th>
                <th
                  class="inventory-table__align-center"
                  scope="col"
                >
                  可用库存
                </th>
                <th
                  class="inventory-table__align-center"
                  scope="col"
                >
                  单位
                </th>
                <th
                  class="inventory-table__align-center"
                  scope="col"
                >
                  库存状态
                </th>
                <th
                  class="inventory-table__align-center"
                  scope="col"
                >
                  操作
                </th>
              </tr>
            </thead>
            <tbody>
              <template
                v-for="sku in filteredSkus"
                :key="sku.id"
              >
                <tr
                  class="inventory-row"
                  :class="{ 'inventory-row--expanded': expandedSkuId === sku.id }"
                >
                  <td class="inventory-table__align-left inventory-code">
                    {{ product.productNo }}
                  </td>
                  <td class="inventory-table__align-left inventory-name">
                    {{ product.name }}
                  </td>
                  <td class="inventory-table__align-left">
                    <span class="inventory-spec">{{ specText(sku) }}</span>
                  </td>
                  <td class="inventory-table__align-center inventory-number">
                    <span class="inventory-number__value">
                      <strong>{{ formatQty(balances[sku.id]?.actualQty) }}</strong>
                    </span>
                  </td>
                  <td class="inventory-table__align-center inventory-number">
                    <span class="inventory-number__value">
                      {{ formatQty(balances[sku.id]?.lockedQty) }}
                    </span>
                  </td>
                  <td class="inventory-table__align-center inventory-number">
                    <span class="inventory-number__value">
                      {{ formatQty(balances[sku.id]?.availableQty) }}
                    </span>
                  </td>
                  <td class="inventory-table__align-center inventory-unit">
                    {{ balances[sku.id]?.baseUnit === "PIECE" || balances[sku.id] == null ? "个" : balances[sku.id]?.baseUnit }}
                  </td>
                  <td class="inventory-table__align-center">
                    <span
                      class="status-badge"
                      :class="statusClass(rowStatus(sku))"
                    >
                      <span
                        aria-hidden="true"
                        class="status-badge__dot"
                      />
                      {{ statusText(rowStatus(sku)) }}
                    </span>
                  </td>
                  <td class="inventory-table__align-center inventory-actions">
                    <div class="inventory-actions__inner">
                      <button
                        :aria-expanded="expandedSkuId === sku.id"
                        class="text-button"
                        type="button"
                        @click="toggleOperations(sku.id)"
                      >
                        {{ expandedSkuId === sku.id ? "收起操作" : rowStatus(sku) === "UNINITIALIZED" ? "初始化" : "调整库存" }}
                      </button>
                    </div>
                  </td>
                </tr>
                <tr
                  v-if="expandedSkuId === sku.id"
                  class="inventory-expanded-row"
                >
                  <td colspan="9">
                    <div class="operation-panel">
                      <div class="operation-panel__header">
                        <div>
                          <strong>{{ specText(sku) }}</strong>
                          <span>库存操作与流水记录</span>
                        </div>
                        <button
                          class="secondary text-button--quiet"
                          type="button"
                          @click="toggleOperations(sku.id)"
                        >
                          收起
                        </button>
                      </div>

                      <form
                        v-if="balances[sku.id] == null"
                        class="operation-form"
                        @submit.prevent="initialize(sku.id)"
                      >
                        <label>
                          初始库存（个）
                          <input
                            v-model.number="formFor(sku.id).initialQty"
                            min="0"
                            required
                            type="number"
                          >
                        </label>
                        <p class="operation-hint">
                          初始化后将生成一条库存初始化流水，后续只能通过调整纠正。
                        </p>
                        <button
                          :disabled="busy"
                          type="submit"
                        >
                          初始化库存
                        </button>
                      </form>

                      <template v-else>
                        <div class="balance-strip">
                          <span>实际库存 <strong>{{ formatQty(balances[sku.id]?.actualQty) }}</strong></span>
                          <span>锁定 <strong>{{ formatQty(balances[sku.id]?.lockedQty) }}</strong></span>
                          <span>可用 <strong>{{ formatQty(balances[sku.id]?.availableQty) }}</strong></span>
                          <span>已售 <strong>{{ formatQty(balances[sku.id]?.soldQty) }}</strong></span>
                        </div>
                        <form
                          class="operation-form operation-form--adjust"
                          @submit.prevent="adjust(sku.id)"
                        >
                          <label>
                            调整类型
                            <select v-model="formFor(sku.id).type">
                              <option value="ADJUST">人工调整</option>
                              <option value="DAMAGE">损坏</option>
                              <option value="LOSS">盘亏</option>
                              <option value="PROFIT">盘盈</option>
                            </select>
                          </label>
                          <label>
                            变化数量
                            <input
                              v-model.number="formFor(sku.id).changeQty"
                              required
                              type="number"
                            >
                          </label>
                          <label class="operation-form__reason">
                            调整原因
                            <input
                              v-model="formFor(sku.id).reason"
                              required
                              type="text"
                            >
                          </label>
                          <button
                            :disabled="busy"
                            type="submit"
                          >
                            确认调整
                          </button>
                          <button
                            class="secondary"
                            type="button"
                            @click="openLogs(sku.id)"
                          >
                            查看流水
                          </button>
                        </form>
                      </template>

                      <div
                        v-if="logs[sku.id]"
                        class="log-panel"
                      >
                        <div class="log-panel__header">
                          <strong>库存流水</strong>
                          <span>{{ logs[sku.id].length }} 条记录</span>
                        </div>
                        <p
                          v-if="!logs[sku.id].length"
                          class="log-empty"
                        >
                          暂无流水记录
                        </p>
                        <template v-if="logs[sku.id].length">
                          <article
                            v-for="log in logs[sku.id]"
                            :key="log.id"
                            class="log-row"
                          >
                            <div>
                              <strong>{{ logTypeText(log.type) }}</strong>
                              <span>{{ log.changeQty > 0 ? "+" : "" }}{{ formatQty(log.changeQty) }}</span>
                            </div>
                            <span>{{ log.beforeActualQty }} → {{ log.afterActualQty }}</span>
                            <span>{{ log.reason || "未填写原因" }}</span>
                            <time :datetime="log.createdAt">{{ formatTime(log.createdAt) }}</time>
                          </article>
                        </template>
                      </div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div
          v-if="!filteredSkus.length"
          class="inventory-empty"
        >
          <strong>没有匹配的 SKU</strong>
          <p>调整搜索词或状态筛选后重试。</p>
          <button
            class="secondary"
            type="button"
            @click="searchQuery = ''; statusFilter = 'ALL'"
          >
            清除筛选
          </button>
        </div>

        <footer class="inventory-table-card__footer">
          <span>显示 {{ filteredSkus.length }} / {{ activeSkus.length }} 个 SKU</span>
          <span>库存数据来自当前商品已加载余额</span>
        </footer>
      </section>
    </template>

    <p
      v-else-if="!busy"
      class="inventory-empty inventory-empty--page"
    >
      暂无可管理商品。
    </p>
  </section>
</template>

<style scoped>
.inventory-page {
  display: grid;
  gap: 24px;
  width: calc(100% - 48px);
  max-width: 1440px;
  margin: 0 auto;
  padding: 36px 0 136px;
}

.inventory-header {
  align-items: end;
}

.inventory-header h1 {
  margin: 0 0 8px;
  font-size: 32px;
  letter-spacing: -0.04em;
}

.inventory-header p {
  margin: 0;
  font-size: 14px;
}

.product-selector {
  display: grid;
  min-width: min(360px, 100%);
  gap: 7px;
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
}

.product-selector select,
.status-filter select,
.operation-form select,
.operation-form input,
.search-field input {
  min-height: 42px;
  border: 1px solid var(--admin-outline);
  border-radius: 9px;
  background: var(--admin-surface);
  color: var(--admin-ink);
}

.product-selector select,
.status-filter select,
.operation-form select,
.operation-form input {
  width: 100%;
  padding: 9px 12px;
}

.inventory-notice {
  margin: 0;
  padding: 12px 16px;
  border: 1px solid rgba(60, 179, 113, 0.35);
  border-radius: 10px;
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
  font-size: 14px;
}

.inventory-loading {
  margin: 0;
  padding: 24px;
  border: 1px dashed var(--admin-outline);
  border-radius: 12px;
  color: var(--admin-muted);
  text-align: center;
}

.inventory-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
}

.metric-card {
  display: grid;
  gap: 14px;
  min-width: 0;
  padding: 20px 22px;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 14px;
  background: var(--admin-surface);
  box-shadow: 0 4px 18px rgba(28, 67, 46, 0.04);
}

.metric-card--actual {
  border-color: rgba(60, 179, 113, 0.35);
}

.metric-card--available {
  border-color: rgba(36, 138, 85, 0.28);
  background: #f6fbf8;
}

.metric-card__topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.metric-card__tag {
  color: var(--admin-primary-deep);
  font-size: 11px;
  font-weight: 600;
}

.metric-card strong {
  color: var(--admin-ink);
  font-size: 32px;
  letter-spacing: -0.04em;
  line-height: 1;
}

.metric-card small {
  min-height: 18px;
  color: var(--admin-muted);
  font-size: 12px;
  line-height: 1.5;
}

.inventory-table-card {
  min-width: 0;
  overflow: hidden;
  border: 1px solid var(--admin-outline-soft);
  border-radius: 14px;
  background: var(--admin-surface);
  box-shadow: 0 4px 18px rgba(28, 67, 46, 0.04);
}

.inventory-table-card__header,
.inventory-table-card__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 22px;
}

.inventory-table-card__header {
  border-bottom: 1px solid var(--admin-outline-soft);
}

.inventory-table-card__header h2 {
  margin: 0 0 6px;
  font-size: 20px;
  letter-spacing: -0.025em;
}

.inventory-table-card__header p {
  margin: 0;
  font-size: 13px;
}

.inventory-filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: end;
  gap: 8px;
}

.search-field {
  position: relative;
  display: flex;
  min-width: min(340px, 100%);
  align-items: center;
}

.search-field__icon {
  position: absolute;
  left: 12px;
  top: 13px;
  width: 12px;
  height: 12px;
  border: 2px solid var(--admin-muted);
  border-radius: 50%;
}

.search-field__icon::after {
  position: absolute;
  right: -5px;
  bottom: -3px;
  width: 6px;
  height: 2px;
  background: var(--admin-muted);
  content: "";
  transform: rotate(45deg);
}

.search-field input {
  width: 100%;
  padding: 9px 12px 9px 36px;
}

.status-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--admin-muted);
  font-size: 13px;
  font-weight: 700;
}

.status-filter select {
  min-width: 106px;
}

.inventory-table-wrap {
  overflow-x: auto;
}

.inventory-table {
  width: 100%;
  min-width: 1080px;
  border-collapse: collapse;
  table-layout: fixed;
  text-align: left;
}

.inventory-table__col--code {
  width: 11%;
}

.inventory-table__col--name {
  width: 15%;
}

.inventory-table__col--spec {
  width: 18%;
}

.inventory-table__col--actual,
.inventory-table__col--locked,
.inventory-table__col--available {
  width: 10%;
}

.inventory-table__col--unit {
  width: 6%;
}

.inventory-table__col--status,
.inventory-table__col--actions {
  width: 10%;
}

.inventory-table th,
.inventory-table td {
  padding: 15px 14px;
  border-bottom: 1px solid var(--admin-outline-soft);
  vertical-align: middle;
}

.inventory-table th {
  background: #f6f8f7;
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.inventory-table td {
  color: var(--admin-ink);
  font-size: 13px;
}

.inventory-table th.inventory-table__align-left,
.inventory-table td.inventory-table__align-left {
  text-align: left;
}

.inventory-table th.inventory-table__align-center,
.inventory-table td.inventory-table__align-center {
  text-align: center;
}

.inventory-row {
  transition: background-color 160ms ease;
}

.inventory-row:hover,
.inventory-row--expanded {
  background: #fbfdfc;
}

.inventory-code {
  color: var(--admin-muted);
  font-family: ui-monospace, SFMono-Regular, Consolas, monospace;
  font-size: 12px;
}

.inventory-name {
  min-width: 150px;
  font-weight: 700;
}

.inventory-spec {
  display: -webkit-box;
  overflow: hidden;
  margin-top: 5px;
  color: var(--admin-muted);
  line-height: 1.45;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.inventory-number {
  min-width: 98px;
  white-space: nowrap;
}

.inventory-number__value {
  display: flex;
  min-height: 38px;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  font-variant-numeric: tabular-nums;
  line-height: 1.4;
}

.inventory-number__value strong {
  font-size: inherit;
  line-height: inherit;
}

.inventory-unit {
  color: var(--admin-muted) !important;
  white-space: nowrap;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 72px;
  justify-content: center;
  padding: 6px 9px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.status-badge__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}

.status-badge--available {
  border-color: rgba(60, 179, 113, 0.24);
  background: #f0faf4;
  color: var(--admin-primary-deep);
}

.status-badge--sold_out {
  border-color: rgba(186, 26, 26, 0.22);
  background: #fff1ef;
  color: #ba1a1a;
}

.status-badge--uninitialized {
  border-color: rgba(191, 129, 0, 0.28);
  background: #fff8e8;
  color: #9a6700;
}

.inventory-actions {
  vertical-align: middle !important;
  white-space: nowrap;
}

.inventory-actions__inner {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
}

.text-button,
.secondary,
.operation-form button,
.inventory-empty button {
  min-height: 38px;
  padding: 8px 13px;
  border: 0;
  border-radius: 8px;
  background: var(--admin-primary);
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.text-button {
  min-height: 34px;
  padding: 6px 10px;
}

.text-button:hover,
.operation-form button:hover,
.inventory-empty button:hover {
  background: var(--admin-primary-deep);
}

.secondary {
  border: 1px solid rgba(36, 138, 85, 0.28);
  background: var(--admin-primary-soft);
  color: var(--admin-primary-deep);
}

.secondary:hover {
  background: #e2f5e9;
}

.text-button--quiet {
  min-height: 34px;
}

.inventory-expanded-row td {
  padding: 0;
  border-bottom: 1px solid var(--admin-outline-soft);
  background: #fbfdfc;
}

.operation-panel {
  display: grid;
  gap: 18px;
  padding: 20px 22px 22px;
  border-top: 2px solid rgba(60, 179, 113, 0.18);
}

.operation-panel__header,
.log-panel__header {
  display: flex;
  align-items: start;
  justify-content: space-between;
  gap: 16px;
}

.operation-panel__header strong,
.log-panel__header strong {
  display: block;
  font-size: 14px;
}

.operation-panel__header span,
.log-panel__header span {
  display: block;
  margin-top: 4px;
  color: var(--admin-muted);
  font-size: 12px;
}

.operation-form {
  display: grid;
  grid-template-columns: minmax(180px, 0.65fr) minmax(150px, 0.5fr) minmax(240px, 1fr) auto;
  align-items: end;
  gap: 12px;
}

.operation-form--adjust {
  grid-template-columns: minmax(150px, 0.65fr) minmax(120px, 0.45fr) minmax(220px, 1fr) auto auto;
}

.operation-form label {
  display: grid;
  gap: 7px;
  color: var(--admin-muted);
  font-size: 12px;
  font-weight: 700;
}

.operation-form__reason {
  min-width: 0;
}

.operation-hint {
  grid-column: 1 / -1;
  margin: -4px 0 0;
  color: var(--admin-muted);
  font-size: 12px;
}

.balance-strip {
  display: flex;
  flex-wrap: wrap;
  gap: 10px 24px;
  padding: 12px 14px;
  border-radius: 9px;
  background: #f3f7f5;
  color: var(--admin-muted);
  font-size: 12px;
}

.balance-strip strong {
  margin-left: 4px;
  color: var(--admin-ink);
  font-size: 14px;
}

.log-panel {
  display: grid;
  gap: 10px;
  padding-top: 16px;
  border-top: 1px solid var(--admin-outline-soft);
}

.log-empty {
  margin: 0;
  color: var(--admin-muted);
  font-size: 13px;
}

.log-row {
  display: grid;
  grid-template-columns: minmax(150px, 0.7fr) minmax(100px, 0.45fr) minmax(220px, 1fr) minmax(90px, auto);
  gap: 14px;
  padding: 10px 0;
  border-top: 1px solid #eef1ef;
  color: var(--admin-muted);
  font-size: 12px;
}

.log-row > div {
  display: flex;
  gap: 8px;
}

.log-row strong {
  color: var(--admin-ink);
}

.log-row time {
  text-align: right;
  white-space: nowrap;
}

.inventory-empty {
  display: grid;
  justify-items: center;
  gap: 8px;
  padding: 36px 20px;
  color: var(--admin-muted);
  text-align: center;
}

.inventory-empty strong {
  color: var(--admin-ink);
}

.inventory-empty p {
  margin: 0 0 4px;
  font-size: 13px;
}

.inventory-empty--page {
  border: 1px dashed var(--admin-outline);
  border-radius: 12px;
}

.inventory-table-card__footer {
  border-top: 1px solid var(--admin-outline-soft);
  color: var(--admin-muted);
  font-size: 12px;
}

button:focus-visible,
input:focus-visible,
select:focus-visible {
  outline: 3px solid rgba(60, 179, 113, 0.25);
  outline-offset: 2px;
}

@media (max-width: 980px) {
  .inventory-table-card__header {
    align-items: start;
    flex-direction: column;
  }

  .inventory-filters {
    width: 100%;
    justify-content: stretch;
  }

  .search-field {
    flex: 1 1 260px;
  }

  .operation-form,
  .operation-form--adjust {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .operation-form__reason,
  .operation-form button:last-child {
    grid-column: 1 / -1;
  }
}

@media (max-width: 900px) {
  .inventory-page {
    width: min(100% - 32px, 720px);
    padding-top: 22px;
  }
}

@media (max-width: 720px) {
  .inventory-page {
    gap: 18px;
  }

  .inventory-header {
    align-items: stretch;
    flex-direction: column;
  }

  .product-selector {
    min-width: 0;
  }

  .inventory-stats {
    grid-template-columns: 1fr;
  }

  .inventory-table-card__header,
  .inventory-table-card__footer {
    padding: 16px;
  }

  .inventory-filters {
    align-items: stretch;
    flex-direction: column;
  }

  .search-field,
  .status-filter {
    width: 100%;
  }

  .status-filter {
    justify-content: space-between;
  }

  .status-filter select {
    flex: 1;
  }

  .operation-panel {
    padding: 18px 16px;
  }

  .operation-form,
  .operation-form--adjust,
  .log-row {
    grid-template-columns: 1fr;
  }

  .operation-form__reason,
  .operation-form button:last-child {
    grid-column: auto;
  }

  .log-row time {
    text-align: left;
  }
}

@media (max-width: 640px) {
  .inventory-page {
    width: min(100% - 24px, 560px);
    padding-bottom: 154px;
  }
}
</style>
